import React, { useState, useEffect } from 'react';
import { PublicNav } from './Landing';
import { Send, Loader2, Mail, MessageSquare, MapPin } from 'lucide-react';
import api from '../api/axios';
import Swal from 'sweetalert2';

const customStyles = `
  @keyframes blob {
    0% { transform: translate(0px, 0px) scale(1); }
    33% { transform: translate(30px, -50px) scale(1.1); }
    66% { transform: translate(-20px, 20px) scale(0.9); }
    100% { transform: translate(0px, 0px) scale(1); }
  }
  .animate-blob { animation: blob 15s infinite alternate ease-in-out; }
`;

const Contact = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSent, setIsSent] = useState(false);
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  
  const [isDark, setIsDark] = useState(() => {
    const storedTheme = localStorage.getItem('theme');
    if (storedTheme) return storedTheme === 'dark';
    if (document.documentElement.classList.contains('dark')) return true;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    if (isDark) { document.documentElement.classList.add('dark'); localStorage.setItem('theme', 'dark'); } 
    else { document.documentElement.classList.remove('dark'); localStorage.setItem('theme', 'light'); }
  }, [isDark]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      await api.post('/contact', {
        name: name,
        email: email,
        message: message
      });
      setIsSent(true);
    } catch (error) {
      // alert("Failed to send message. Please try again.");
      Swal.fire({
        icon: 'error',
        title: 'Failed to send message',
        text: 'Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-slate-50 dark:bg-[#05050A] text-slate-900 dark:text-slate-200 font-sans relative overflow-hidden transition-colors duration-500 pt-24 flex flex-col">
      <style>{customStyles}</style>
      
      {/* Background Blobs */}
      <div className="absolute top-[20%] left-[-10%] w-[50vw] h-[50vh] rounded-full bg-indigo-600/10 blur-[120px] animate-blob z-0 pointer-events-none"></div>
      <div className="absolute bottom-[10%] right-[-10%] w-[50vw] h-[50vh] rounded-full bg-violet-600/10 blur-[120px] animate-blob z-0 pointer-events-none" style={{ animationDelay: '2s' }}></div>

      <PublicNav isDark={isDark} setIsDark={setIsDark} />

      <div className="max-w-6xl mx-auto px-6 w-full relative z-10 py-12 lg:py-20 flex-1 flex flex-col justify-center">
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-start">
          
          {/* LEFT COLUMN: Contact Info & Copy */}
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-700">
            <span className="text-[12px] font-bold tracking-wide text-indigo-600 dark:text-indigo-400 flex items-center gap-2 mb-4">
              <div className="w-4 h-0.5 bg-indigo-600 dark:bg-indigo-400"></div> GET IN TOUCH
            </span>
            <h1 className="text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 dark:text-white mb-6 leading-tight">
              Let's start a <br/><span className="font-medium bg-gradient-to-r from-indigo-500 to-violet-500 bg-clip-text text-transparent">conversation.</span>
            </h1>
            <p className="text-base font-light text-slate-600 dark:text-slate-400 mb-12 leading-relaxed max-w-md">
              Whether you are looking to scale your agency's screening process, need a custom enterprise plan, or just want a product tour—our team is ready to help.
            </p>

            <div className="space-y-8">
              <div className="flex items-start gap-5 group">
                <div className="w-12 h-12 rounded-xl bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0 border border-indigo-100 dark:border-indigo-500/20 group-hover:scale-105 transition-transform">
                  <MessageSquare size={20} strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-1">Chat with Sales</h3>
                  <p className="text-xs font-light text-slate-500 dark:text-slate-400 mb-2 leading-relaxed">Speak directly to our team about pricing and features.</p>
                  <a href="mailto:sales@recruitai.com" className="text-xs font-bold tracking-wide text-indigo-600 dark:text-indigo-400 hover:underline">sales@recruitai.com</a>
                </div>
              </div>

              <div className="flex items-start gap-5 group">
                <div className="w-12 h-12 rounded-xl bg-violet-50 dark:bg-violet-500/10 text-violet-600 dark:text-violet-400 flex items-center justify-center shrink-0 border border-violet-100 dark:border-violet-500/20 group-hover:scale-105 transition-transform">
                  <Mail size={20} strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-1">Support & Help</h3>
                  <p className="text-xs font-light text-slate-500 dark:text-slate-400 mb-2 leading-relaxed">Current customers, get technical assistance here.</p>
                  <a href="mailto:support@recruitai.com" className="text-xs font-bold tracking-wide text-violet-600 dark:text-violet-400 hover:underline">support@recruitai.com</a>
                </div>
              </div>

              <div className="flex items-start gap-5 group">
                <div className="w-12 h-12 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0 border border-emerald-100 dark:border-emerald-500/20 group-hover:scale-105 transition-transform">
                  <MapPin size={20} strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-1">Global Headquarters</h3>
                  <p className="text-xs font-light text-slate-500 dark:text-slate-400 leading-relaxed">Pune, Maharashtra<br/>India</p>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: The Form */}
          <div className="animate-in fade-in zoom-in-95 duration-700" style={{ animationDelay: '200ms' }}>
            {isSent ? (
              <div className="bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 p-10 rounded-[2rem] text-center shadow-xl">
                <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Send size={24} />
                </div>
                <h3 className="text-2xl font-medium text-emerald-900 dark:text-emerald-300 mb-3">Message Received!</h3>
                <p className="text-sm text-emerald-700 dark:text-emerald-400/80 font-light leading-relaxed">
                  Thank you for reaching out, {name.split(' ')[0]}. Our team will review your message and get back to you at {email} within 24 hours.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white/80 dark:bg-[#0a0a0a]/60 backdrop-blur-xl p-8 sm:p-10 rounded-[2rem] border border-slate-200 dark:border-white/10 shadow-2xl flex flex-col gap-6 relative">
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-[12px] font-bold text-slate-500 dark:text-slate-400 tracking-wide mb-2">Name</label>
                    <input 
                      type="text" 
                      required 
                      disabled={isLoading} 
                      value={name}                                      
                      onChange={(e) => setName(e.target.value)}
                      placeholder="e.g. John Doe"
                      className="w-full bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-sm placeholder:text-[12px] font-medium text-slate-900 dark:text-white focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all disabled:opacity-50" 
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] font-bold text-slate-500 dark:text-slate-400 tracking-wide mb-2">Work Email</label>
                    <input 
                      type="email" 
                      required 
                      disabled={isLoading} 
                      value={email}                                     
                      onChange={(e) => setEmail(e.target.value)}        
                      placeholder="you@company.com"
                      className="w-full bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-sm placeholder:text-[12px] font-medium text-slate-900 dark:text-white focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all disabled:opacity-50" 
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[12px] font-bold text-slate-500 dark:text-slate-400 tracking-wide mb-2">How can we help?</label>
                  <textarea 
                    required 
                    disabled={isLoading} 
                    rows="5" 
                    value={message}                                   
                    onChange={(e) => setMessage(e.target.value)}      
                    placeholder="Tell us about your agency's volume, current ATS, and what you're looking to achieve..."
                    className="w-full bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-sm placeholder:text-[12px] font-medium text-slate-900 dark:text-white focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all resize-none disabled:opacity-50"
                  ></textarea>
                </div>

                <button 
                  type="submit" 
                  disabled={isLoading || !name || !email || !message} 
                  className="w-full bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl py-4 text-xs font-bold tracking-wide transition-all shadow-[0_0_15px_rgba(99,102,241,0.3)] active:scale-95 flex items-center justify-center gap-3 disabled:opacity-50 mt-2"
                >
                  {isLoading ? <Loader2 size={16} className="animate-spin" /> : <><Send size={16} /> Send Message</>}
                </button>

              </form>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default Contact;